import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const EMOJI = { 'AB Βασιλόπουλος': '🛒', 'Coffee Island': '☕', 'Δήμος Θεσσαλονίκης': '🏛️', 'WWF Ελλάς': '🌿', 'Αρχαιολογικό Μουσείο Θεσσαλονίκης': '🏺' }

export default function RewardsPage() {
  const { profile, refreshProfile } = useAuth()
  const [rewards, setRewards] = useState([])
  const [redemptions, setRedemptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [redeeming, setRedeeming] = useState(null)
  const [toast, setToast] = useState('')

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    const [{ data: r }, { data: d }] = await Promise.all([
      supabase.from('rewards').select('*').eq('active', true).order('points_cost'),
      supabase.from('redemptions').select('*, rewards(name)').eq('user_id', profile?.id).order('redeemed_at', { ascending: false }).limit(5)
    ])
    setRewards(r || [])
    setRedemptions(d || [])
    setLoading(false)
  }

  async function handleRedeem(reward) {
    if ((profile?.points || 0) < reward.points_cost) return
    setRedeeming(reward.id)

    const { error } = await supabase.from('redemptions').insert({
      user_id: profile.id,
      reward_id: reward.id,
      points_spent: reward.points_cost
    })
    if (error) { setRedeeming(null); return }

    await supabase.from('profiles').update({
      points: profile.points - reward.points_cost
    }).eq('id', profile.id)

    await refreshProfile()
    await fetchData()
    setRedeeming(null)
    showToast(`✓ Εξαργυρώθηκε: ${reward.name}`)
  }

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const points = profile?.points || 0

  return (
    <div style={s.page}>
      {toast && <div style={s.toast}>{toast}</div>}

      {/* Points banner */}
      <div style={s.banner}>
        <div style={s.bannerVal}>{points}</div>
        <div style={s.bannerLabel}>διαθέσιμοι πόντοι</div>
      </div>

      {/* Rewards list */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Διαθέσιμες επιβραβεύσεις</h3>
        {loading ? <div style={s.loading}>Φόρτωση...</div> : (
          <div style={s.rewardList}>
            {rewards.map(reward => {
              const canAfford = points >= reward.points_cost
              return (
                <div key={reward.id} style={s.rewardItem}>
                  <div style={s.rewardIcon}>{EMOJI[reward.partner] || '🎁'}</div>
                  <div style={s.rewardInfo}>
                    <div style={s.rewardName}>{reward.name}</div>
                    <div style={s.rewardPartner}>{reward.partner}</div>
                    {reward.description && <div style={s.rewardDesc}>{reward.description}</div>}
                    <div style={s.rewardCost}>{reward.points_cost} πόντοι</div>
                  </div>
                  <button
                    style={{ ...s.redeemBtn, ...(canAfford ? s.redeemActive : s.redeemDisabled) }}
                    onClick={() => canAfford && handleRedeem(reward)}
                    disabled={!canAfford || redeeming === reward.id}
                  >
                    {redeeming === reward.id ? '...' : (canAfford ? 'Εξαργύρωση' : `${reward.points_cost - points} ακόμα`)}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* History */}
      {redemptions.length > 0 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Ιστορικό εξαργυρώσεων</h3>
          {redemptions.map(r => (
            <div key={r.id} style={s.histItem}>
              <div style={s.histName}>{r.rewards?.name}</div>
              <div style={s.histMeta}>
                <span style={s.histDate}>{new Date(r.redeemed_at).toLocaleDateString('el-GR')}</span>
                <span style={s.histPts}>-{r.points_spent} πτ</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const s = {
  page: { padding: '1rem', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative' },
  toast: { position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#1A6B3C', color: 'white', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 100, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  banner: { background: '#1A6B3C', borderRadius: 16, padding: '1.5rem', textAlign: 'center', color: 'white' },
  bannerVal: { fontSize: 48, fontWeight: 700, lineHeight: 1 },
  bannerLabel: { fontSize: 14, opacity: 0.85, marginTop: 6 },
  card: { background: 'white', borderRadius: 16, padding: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#333' },
  loading: { textAlign: 'center', color: '#aaa', padding: '2rem 0' },
  rewardList: { display: 'flex', flexDirection: 'column', gap: 0 },
  rewardItem: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0', borderBottom: '1px solid #f5f5f5' },
  rewardIcon: { fontSize: 32, flexShrink: 0, width: 44, textAlign: 'center' },
  rewardInfo: { flex: 1 },
  rewardName: { fontWeight: 600, color: '#333', fontSize: 14 },
  rewardPartner: { fontSize: 12, color: '#888', marginTop: 2 },
  rewardDesc: { fontSize: 12, color: '#aaa', marginTop: 2 },
  rewardCost: { fontSize: 13, fontWeight: 600, color: '#1A6B3C', marginTop: 4 },
  redeemBtn: { padding: '8px 12px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 4 },
  redeemActive: { background: '#1A6B3C', color: 'white' },
  redeemDisabled: { background: '#f5f5f5', color: '#aaa', cursor: 'default' },
  histItem: { padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  histName: { fontSize: 14, color: '#333', fontWeight: 500 },
  histMeta: { display: 'flex', justifyContent: 'space-between', marginTop: 4 },
  histDate: { fontSize: 12, color: '#aaa' },
  histPts: { fontSize: 12, fontWeight: 600, color: '#dc2626' },
}
